import { useEffect, useState } from "react";
import { useT } from "../i18n/hooks.ts";

/**
 * First/previous/next/last + a jump-to-page input — for a list too long to
 * browse comfortably as one continuous scroll. Replaces an earlier
 * scroll-virtualization approach (AemetBulkFetchDialog.tsx's row list):
 * real usability problem reported live — with well over a thousand rows
 * (a real MITECO import, the Duero: 1728 sub-basins), the scrollbar's own
 * thumb becomes a sliver of the track, making it nearly impossible to
 * navigate precisely by dragging it. Pagination stays equally easy to use
 * regardless of how many pages there are, and needs no scroll tracking at
 * all — simpler than what it replaced, not just a different trade-off.
 *
 * `page` is 0-indexed internally (matches `Array.slice`'s own indexing at
 * the call site) — shown to the user as 1-indexed.
 */
export function Pagination({
  page,
  pageCount,
  onPageChange,
}: {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
}) {
  const t = useT();
  const [jumpValue, setJumpValue] = useState(String(page + 1));

  useEffect(() => {
    setJumpValue(String(page + 1));
  }, [page]);

  if (pageCount <= 1) return null;

  function commitJump() {
    const n = Math.trunc(Number(jumpValue));
    if (Number.isFinite(n) && n >= 1 && n <= pageCount) onPageChange(n - 1);
    else setJumpValue(String(page + 1));
  }

  return (
    <div className="pagination">
      <button
        className="button-icon"
        disabled={page === 0}
        title={t("pagination.first")}
        onClick={() => onPageChange(0)}
      >
        «
      </button>
      <button
        className="button-icon"
        disabled={page === 0}
        title={t("pagination.previous")}
        onClick={() => onPageChange(page - 1)}
      >
        ‹
      </button>
      <span className="pagination-jump">
        {t("pagination.page")}{" "}
        <input
          type="number"
          min={1}
          max={pageCount}
          value={jumpValue}
          onChange={(e) => setJumpValue(e.target.value)}
          onBlur={commitJump}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitJump();
          }}
        />{" "}
        {t("pagination.of", { total: String(pageCount) })}
      </span>
      <button
        className="button-icon"
        disabled={page >= pageCount - 1}
        title={t("pagination.next")}
        onClick={() => onPageChange(page + 1)}
      >
        ›
      </button>
      <button
        className="button-icon"
        disabled={page >= pageCount - 1}
        title={t("pagination.last")}
        onClick={() => onPageChange(pageCount - 1)}
      >
        »
      </button>
    </div>
  );
}
